// Client-side hooks for the /api/audit/* endpoints. Three behaviours:
//
//   1. NavigationTracker     — React component that POSTs every route change.
//   2. startAuditHeartbeat   — interval that POSTs heartbeat (default 90s).
//   3. attachBeaconLogout    — beforeunload handler that fires a beacon so the
//                              session row gets stamped with logout_reason=tab_close
//                              even when the user just closes the tab.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

function getToken() {
  return localStorage.getItem('token');
}

// Fire-and-forget navigation log. Failures are silent — the user shouldn't
// see audit infrastructure errors in their console.
async function postNavigate(path) {
  const token = getToken();
  if (!token) return;
  try {
    await fetch(`${API_URL}/api/audit/navigate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ path })
    });
  } catch (_) {
    /* swallow */
  }
}

async function postHeartbeat() {
  const token = getToken();
  if (!token) return;
  try {
    await fetch(`${API_URL}/api/audit/heartbeat`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (_) {
    /* swallow */
  }
}

// Logs each pathname change. Mounted once, inside <Router>, after auth.
// Uses a debounce timer so a burst of redirects (e.g. login → home → role-home)
// only emits the final destination.
export function NavigationTracker() {
  const location = useLocation();
  const timerRef = useRef(null);
  const lastSentRef = useRef(null);

  useEffect(() => {
    const path = location.pathname + (location.search || '');
    if (lastSentRef.current === path) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastSentRef.current = path;
      postNavigate(path);
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [location.pathname, location.search]);

  return null;
}

let heartbeatTimer = null;

export function startAuditHeartbeat(intervalMs = 90 * 1000) {
  stopAuditHeartbeat();
  // Fire one immediately so the first heartbeat doesn't have to wait 90s after
  // login. Subsequent fires are on the interval.
  postHeartbeat();
  heartbeatTimer = setInterval(postHeartbeat, intervalMs);
}

export function stopAuditHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

let beaconHandler = null;

export function attachBeaconLogout() {
  detachBeaconLogout();
  beaconHandler = () => {
    const token = getToken();
    if (!token) return;
    // navigator.sendBeacon can't set headers — pass the JWT as ?t=<token>.
    // The body is empty but Blob with JSON content-type keeps Express happy.
    try {
      const url = `${API_URL}/api/audit/beacon-logout?t=${encodeURIComponent(token)}`;
      const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } catch (_) {
      /* swallow */
    }
  };
  window.addEventListener('beforeunload', beaconHandler);
  // pagehide fires on mobile Safari when the user switches tabs — important
  // because beforeunload isn't reliable on iOS.
  window.addEventListener('pagehide', beaconHandler);
}

export function detachBeaconLogout() {
  if (beaconHandler) {
    window.removeEventListener('beforeunload', beaconHandler);
    window.removeEventListener('pagehide', beaconHandler);
    beaconHandler = null;
  }
}
